/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Parametres\Fournitures.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
SELLERIE MARINE - COMPOSANT GESTION FOURNITURES

📋 DESCRIPTION:
Composant React pour la gestion complète des fournitures (fermetures et petites fournitures).
Interface d'administration pour catalogue produits avec CRUD complet.

🎯 RESPONSABILITÉS:
- Gestion des fermetures (glissières, velcro) avec tailles et prix
- Gestion des petites fournitures (œillets, accessoires divers)
- Interface CRUD complète (création, lecture, suppression)
- Calcul automatique des marges fournisseurs
- Validation des données avec messages d'erreur/succès
- Formatage automatique des prix en euros

🔗 DÉPENDANCES:
- ParametreService: Service de gestion des fournitures
- React: Hooks useState, useEffect pour gestion d'état
- Intl: API de formatage des prix en devise locale

📡 INTERACTIONS:
- ParametreService: CRUD fournitures via API backend
- Interface: Formulaires de saisie avec validation
- Utilisateur: Actions d'ajout/suppression avec confirmations
- Système: Messages de feedback pour actions utilisateur

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Séparation fermetures vs petites fournitures pour organisation
- Bouton automatique pour ajout œillets avec valeur par défaut
- Validation obligatoire des champs critiques (nom, prix)
- Formatage prix avec Intl.NumberFormat pour localisation
- Interface responsive avec tableaux adaptatifs
- Gestion des marges fournisseurs pour calcul MB
*/
import React, { useState, useEffect } from 'react';
import { parametreService, Fourniture } from '../../services/ParametreService';

// Types pour les fournitures
interface Fermeture {
  id: string;
  nom: string;
  type: 'glissiere' | 'velcro';
  taille: number;
  prix: number;
  unite: string;
  description: string;
  fournisseur?: string;
  reference?: string;
  couleurs?: string[];
  stock?: number;
  seuil_alerte?: number;
  marge?: number;
}

interface PetiteFourniture extends Fourniture {
  unite: string;
}

const Fournitures: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [fermetures, setFermetures] = useState<Fermeture[]>([]);
  const [petitesFournitures, setPetitesFournitures] = useState<PetiteFourniture[]>([]);
  const [nouvelleFermeture, setNouvelleFermeture] = useState<Partial<Fermeture>>({
    type: 'glissiere',
    taille: 0,
    prix: 0,
    reference: '',
    marge: 30, // Valeur par défaut de 30%
  });
  const [nouvellePetiteFourniture, setNouvellePetiteFourniture] = useState<Partial<PetiteFourniture>>({
    nom: '',
    prix: 0,
    unite: 'unité',
  });
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Charger les données depuis le service
  useEffect(() => {
    const loadFournitures = async () => {
      try {
        // Charger toutes les fournitures via le ParametreService
        const fournitures = await parametreService.getFournitures();
        
        // Séparer les fermetures des autres fournitures
        const fermeturesData = fournitures.filter(f => f.type === 'glissiere' || f.type === 'velcro') as Fermeture[];
        const petitesFournituresData = fournitures.filter(f => f.type !== 'glissiere' && f.type !== 'velcro') as PetiteFourniture[];
        
        setFermetures(fermeturesData);
        setPetitesFournitures(petitesFournituresData);
        
      } catch (error) {
        console.error('Erreur lors du chargement des fournitures:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadFournitures();
  }, []);

  // Gérer l'ajout d'une nouvelle fermeture
  const ajouterFermeture = async () => {
    if (!nouvelleFermeture.taille || !nouvelleFermeture.prix) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (!nouvelleFermeture.reference) {
      alert('Veuillez renseigner une référence');
      return;
    }

    try {
      setLoading(true);
      
      const newFermeture: Fermeture = {
        id: Date.now().toString(),
        nom: `${nouvelleFermeture.type === 'glissiere' ? 'Fermeture à glissière' : 'Velcro'} ${nouvelleFermeture.taille}cm`,
        type: nouvelleFermeture.type as 'glissiere' | 'velcro',
        taille: nouvelleFermeture.taille,
        prix: nouvelleFermeture.prix,
        unite: 'unité',
        reference: nouvelleFermeture.reference || '',
        description: `${nouvelleFermeture.type === 'glissiere' ? 'Fermeture à glissière' : 'Velcro'} de ${nouvelleFermeture.taille} cm`
      };

      // Sauvegarder la fermeture via le service
      await parametreService.saveFourniture(newFermeture);
      
      // Recharger les fournitures
      const fournitures = await parametreService.getFournitures();
      const fermeturesData = fournitures.filter(f => f.type === 'glissiere' || f.type === 'velcro') as Fermeture[];
      setFermetures(fermeturesData);
      
      // Réinitialiser le formulaire
      setNouvelleFermeture({
        type: 'glissiere',
        taille: 0,
        prix: 0,
        reference: '',
      });
      
      setSuccessMessage('Fermeture ajoutée avec succès!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la fermeture:', error);
      alert('Une erreur est survenue lors de l\'ajout de la fermeture');
    } finally {
      setLoading(false);
    }
  };

  // Gérer l'ajout d'une nouvelle petite fourniture
  const ajouterPetiteFourniture = async () => {
    if (!nouvellePetiteFourniture.nom || !nouvellePetiteFourniture.prix) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setLoading(true);
      
      const newPetiteFourniture: PetiteFourniture = {
        id: Date.now().toString(),
        nom: nouvellePetiteFourniture.nom,
        prix: nouvellePetiteFourniture.prix,
        type: 'petite_fourniture',
        unite: nouvellePetiteFourniture.unite || 'unité',
        description: `${nouvellePetiteFourniture.nom} (${nouvellePetiteFourniture.unite || 'unité'})`
      };

      // Sauvegarder la petite fourniture via le service
      await parametreService.saveFourniture(newPetiteFourniture);
      
      // Recharger les fournitures
      const fournitures = await parametreService.getFournitures();
      const petitesFournituresData = fournitures.filter(f => f.type !== 'glissiere' && f.type !== 'velcro') as PetiteFourniture[];
      setPetitesFournitures(petitesFournituresData);
      
      // Réinitialiser le formulaire
      setNouvellePetiteFourniture({
        nom: '',
        prix: 0,
        unite: 'unité',
      });
      
      setSuccessMessage('Petite fourniture ajoutée avec succès!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la petite fourniture:', error);
      alert('Une erreur est survenue lors de l\'ajout de la petite fourniture');
    } finally {
      setLoading(false);
    }
  };

  // Gérer la suppression d'une fermeture
  const supprimerFermeture = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette fermeture ?')) {
      try {
        setLoading(true);
        
        // Supprimer la fermeture via le service
        await parametreService.deleteFourniture(id);
        
        // Recharger les fournitures
        const fournitures = await parametreService.getFournitures();
        const fermeturesData = fournitures.filter(f => f.type === 'glissiere' || f.type === 'velcro') as Fermeture[];
        setFermetures(fermeturesData);
        
        setSuccessMessage('Fermeture supprimée avec succès!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (error) {
        console.error('Erreur lors de la suppression de la fermeture:', error);
        alert('Une erreur est survenue lors de la suppression de la fermeture');
      } finally {
        setLoading(false);
      }
    }
  };

  // Gérer la suppression d'une petite fourniture
  const supprimerPetiteFourniture = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette petite fourniture ?')) {
      try {
        setLoading(true);
        
        // Supprimer la petite fourniture via le service
        await parametreService.deleteFourniture(id);
        
        // Recharger les fournitures
        const fournitures = await parametreService.getFournitures();
        const petitesFournituresData = fournitures.filter(f => f.type !== 'glissiere' && f.type !== 'velcro') as PetiteFourniture[];
        setPetitesFournitures(petitesFournituresData);
        
        setSuccessMessage('Petite fourniture supprimée avec succès!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (error) {
        console.error('Erreur lors de la suppression de la petite fourniture:', error);
        alert('Une erreur est survenue lors de la suppression de la petite fourniture');
      } finally {
        setLoading(false);
      }
    }
  };

  // Fonction pour formater les prix
  const formatPrix = (prix: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(prix);
  };

  const isDemoFermeture = (f: Fermeture) => {
    const nom = (f.nom || '').toLowerCase();
    const ref = (f.reference || '').toLowerCase();
    return nom.includes('demo') || ref.includes('démo') || ref.includes('demo');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des fournitures...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-xl font-medium text-gray-900 mb-6">Gestion des fournitures</h2>

      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
          {successMessage}
        </div>
      )}

      {/* Section Fermetures */}
      <div className="bg-gray-50 p-4 rounded-lg mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Fermetures à glissière et velcro</h3>

        {/* Formulaire d'ajout de fermeture */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
          <div>
            <label htmlFor="type-fermeture" className="form-label">
              Type
            </label>
            <small className="text-gray-500 mt-1 block h-10">
              Type de fermeture à utiliser
            </small>
            <select
              id="type-fermeture"
              className="input-field"
              value={nouvelleFermeture.type}
              onChange={(e) => setNouvelleFermeture({ ...nouvelleFermeture, type: e.target.value as 'glissiere' | 'velcro' })}
            >
              <option value="glissiere">Fermeture à glissière</option>
              <option value="velcro">Velcro</option>
            </select>
          </div>
          <div>
            <label htmlFor="taille-fermeture" className="form-label">
              Taille (cm)
            </label>
            <small className="text-gray-500 mt-1 block h-10">
              Longueur de la fermeture
            </small>
            <input
              type="number"
              id="taille-fermeture"
              className="input-field"
              value={nouvelleFermeture.taille || ''}
              onChange={(e) => setNouvelleFermeture({ ...nouvelleFermeture, taille: Number(e.target.value) })}
            />
          </div>
          <div>
            <label htmlFor="prix-fermeture" className="form-label">
              Prix TTC (€)
            </label>
            <small className="text-gray-500 mt-1 block h-10">
              Saisissez le prix public TTC
            </small>
            <input
              type="number"
              id="prix-fermeture"
              step="0.01"
              className="input-field"
              value={nouvelleFermeture.prix || ''}
              onChange={(e) => setNouvelleFermeture({ ...nouvelleFermeture, prix: Number(e.target.value) })}
            />
          </div>
          <div>
            <label htmlFor="reference-fermeture" className="form-label">
              Référence
            </label>
            <small className="text-gray-500 mt-1 block h-10">
              Référence fournisseur / interne
            </small>
            <input
              type="text"
              id="reference-fermeture"
              className="input-field"
              value={nouvelleFermeture.reference || ''}
              onChange={(e) => setNouvelleFermeture({ ...nouvelleFermeture, reference: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="marge-fermeture" className="form-label">
              Remise accordée par le fournisseur (%)
            </label>
            <small className="text-gray-500 mt-1 block h-10">
              Pourcentage de remise accordée par le fournisseur
            </small>
            <input
              type="number"
              id="marge-fermeture"
              step="1"
              min="0"
              max="100"
              className="input-field"
              value={nouvelleFermeture.marge || 30}
              onChange={(e) => setNouvelleFermeture({ ...nouvelleFermeture, marge: Number(e.target.value) })}
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              className="btn-primary w-full"
              onClick={ajouterFermeture}
            >
              Ajouter
            </button>
          </div>
        </div>

        {/* Liste des fermetures */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Libellé / réf.
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Taille (cm)
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prix
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Remise (%)
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {fermetures.map((fermeture) => (
                <tr key={fermeture.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {fermeture.type === 'glissiere' ? 'Fermeture à glissière' : 'Velcro'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center gap-2">
                      <span>{fermeture.nom || '-'}</span>
                      {isDemoFermeture(fermeture) && (
                        <span className="text-xs font-semibold text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">
                          Démo
                        </span>
                      )}
                    </div>
                    {fermeture.reference && (
                      <div className="text-xs text-gray-500">{fermeture.reference}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {fermeture.taille} cm
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatPrix(fermeture.prix)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {fermeture.marge ? `${fermeture.marge}%` : '30%'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      className="text-red-600 hover:text-red-900"
                      onClick={() => fermeture.id && supprimerFermeture(fermeture.id)}
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section Petites Fournitures */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Petites fournitures</h3>

        {/* Bouton pour ajouter automatiquement les œillets */}
        <div className="mb-4">
          <button
            type="button"
            className="btn-secondary"
            onClick={async () => {
              try {
                setLoading(true);
                
                // Vérifier si les œillets existent déjà
                const fournitures = await parametreService.getFournitures();
                const oeilletsExistants = fournitures.find(f => f.nom === 'Œillets (pose comprise)');
                
                if (!oeilletsExistants) {
                  // Créer la fourniture pour les œillets
                  const oeillets: PetiteFourniture = {
                    id: Date.now().toString(),
                    nom: 'Œillets (pose comprise)',
                    prix: 3, // Valeur par défaut à 3 €
                    type: 'petite_fourniture',
                    unite: 'unité',
                    description: 'Œillets avec pose comprise pour bâches'
                  };
                  
                  // Sauvegarder via le service
                  await parametreService.saveFourniture(oeillets);
                  
                  // Recharger les fournitures
                  const fournituresUpdated = await parametreService.getFournitures();
                  const petitesFournituresData = fournituresUpdated.filter(f => f.type !== 'glissiere' && f.type !== 'velcro') as PetiteFourniture[];
                  setPetitesFournitures(petitesFournituresData);
                  
                  setSuccessMessage('Œillets ajoutés avec succès!');
                } else {
                  setSuccessMessage('Les œillets existent déjà dans la liste des fournitures.');
                }
                
                setTimeout(() => setSuccessMessage(''), 3000);
              } catch (error) {
                console.error('Erreur lors de l\'ajout des œillets:', error);
                alert('Une erreur est survenue lors de l\'ajout des œillets');
              } finally {
                setLoading(false);
              }
            }}
          >
            Ajouter les œillets (pose comprise)
          </button>
          <small className="text-gray-500 mt-1 block">
            Ajoute automatiquement les œillets avec une valeur par défaut de 3 € par unité
          </small>
        </div>

        {/* Formulaire d'ajout de petite fourniture */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div>
            <label htmlFor="nom-fourniture" className="form-label">
              Nom
            </label>
            <small className="text-gray-500 mt-1 block h-10">
              Nom de la fourniture
            </small>
            <input
              type="text"
              id="nom-fourniture"
              className="input-field"
              value={nouvellePetiteFourniture.nom}
              onChange={(e) => setNouvellePetiteFourniture({ ...nouvellePetiteFourniture, nom: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="prix-fourniture" className="form-label">
              Prix TTC (€)
            </label>
            <small className="text-gray-500 mt-1 block h-10">
              Saisissez le prix public TTC
            </small>
            <input
              type="number"
              id="prix-fourniture"
              step="0.01"
              className="input-field"
              value={nouvellePetiteFourniture.prix || ''}
              onChange={(e) => setNouvellePetiteFourniture({ ...nouvellePetiteFourniture, prix: Number(e.target.value) })}
            />
          </div>
          <div>
            <label htmlFor="unite-fourniture" className="form-label">
              Unité
            </label>
            <small className="text-gray-500 mt-1 block h-10">
              Unité de mesure de la fourniture
            </small>
            <select
              id="unite-fourniture"
              className="input-field"
              value={nouvellePetiteFourniture.unite}
              onChange={(e) => setNouvellePetiteFourniture({ ...nouvellePetiteFourniture, unite: e.target.value })}
            >
              <option value="unité">Unité</option>
              <option value="mètre">Mètre</option>
              <option value="kg">Kilogramme</option>
              <option value="lot">Lot</option>
            </select>
          </div>
          <div>
            {/* Espace vide pour aligner avec le formulaire des fermetures */}
          </div>
          <div className="flex items-end">
            <button
              type="button"
              className="btn-primary w-full"
              onClick={ajouterPetiteFourniture}
            >
              Ajouter
            </button>
          </div>
        </div>

        {/* Liste des petites fournitures */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prix
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unité
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {petitesFournitures.map((fourniture) => (
                <tr key={fourniture.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {fourniture.nom}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatPrix(fourniture.prix)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {fourniture.unite}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      className="text-red-600 hover:text-red-900"
                      onClick={() => fourniture.id && supprimerPetiteFourniture(fourniture.id)}
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Fournitures;
